import { useMemo } from 'react';
import type { NextComponentType, NextPageContext } from 'next';
import type { AppProps } from 'next/app';
import type { ApolloError, DocumentNode, InMemoryCacheConfig, NormalizedCacheObject } from '@apollo/client';
import { ApolloClient, ApolloLink, concat, HttpLink, InMemoryCache, split } from '@apollo/client';
import type { NetworkError } from '@apollo/client/errors';
import { captureException, captureMessage, withScope } from '@sentry/browser';
import { onError } from '@apollo/client/link/error';
import { WebSocketLink } from '@apollo/client/link/ws';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import type { ClientOptions } from 'graphql-ws';
import { createClient } from 'graphql-ws';
import type { SubscriptionClient } from 'subscriptions-transport-ws';
import fetch from 'cross-fetch';
import includes from 'lodash/includes';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import compact from 'lodash/compact';
import merge from 'deepmerge';
import { getMainDefinition } from '@apollo/client/utilities';
import map from 'lodash/map';
import type { GraphQLError } from 'graphql';
import Cookie from 'cookie';

import { getDebugLogger } from '@jerry-serverless/console/debug';
import { timeout, wait } from '@jerry-serverless/promise';
import type { ClientNameEnum } from '@jerry-serverless/async-local-storage/enums';

import { isIDField, toLocalIDFilter } from '../node';
import { DISABLE_GLOBAL_ID_HEADER } from '../middlewares';

const debug = getDebugLogger('deploy:apollo-client');
const retryInfo = getDebugLogger('deploy:apollo-client-retry');

let apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

const GATEWAY_ERRORS = [502, 503, 504];
const MAX_RETRIES = 10;
const FETCH_TIMEOUT = 30 * 1000;

const RETRY_DELAYS = [1000, 2000, 5000];
export const JERRY_REQUEST_SOURCE_HEADER = 'X-jerry-request-source';

export const COGNITO_ID_TOKEN_HEADER_NAME = 'x-jerry-id-token';
export const CLIENT_HEADER_NAME = 'x-jerry-client';
export interface ApolloPageProps {
  initialApolloState?: NormalizedCacheObject;
}

export interface ApolloAppProps extends AppProps {
  Component: NextComponentType<NextPageContext, unknown, ApolloPageProps>;
  pageProps: ApolloPageProps;
}

/**
 * Parses the different types of errors from `ApolloError` and returns
 * a message that can be displayed to the user.
 *
 * @param error
 */
export const parseError = (error: ApolloError | Error): string => {
  const { graphQLErrors, networkError, message } = error as ApolloError;
  if (!isEmpty(graphQLErrors)) {
    // Check if there is error data in extensions
    const respErrors = get(graphQLErrors[0], 'extensions.exception.response.message');
    if (respErrors) {
      if (isArray(respErrors)) return respErrors.join('; ');
      return respErrors;
    }

    // Otherwise send the message
    return graphQLErrors[0].message;
  }

  if (networkError) {
    if (includes(GATEWAY_ERRORS, get(networkError, 'statusCode'))) {
      return 'Service is currently unavailable. Please try again in a few seconds';
    }
    // Error detail is in the result
    const errors = get(networkError, 'result.errors');
    if (!isEmpty(errors)) {
      return map(errors, 'message').join('; ');
    }
    return networkError.message;
  }

  return message;
};

/**
 * This function is a wrapper around `fetch`. It adds retry logic when we get
 * gateway errors from backend (gateway not available). This generally happens either when
 * the server is being deployed or if it is too busy.
 */
export const fetchWithRetry = async (
  req: RequestInfo | URL,
  reqinit: RequestInit | undefined,
  tryNum = 0,
  timeoutMs = FETCH_TIMEOUT
): Promise<Response> => {
  const resp = await timeout(timeoutMs, fetch(req, reqinit))
    // We should also check Server Error or Server Parse Error.
    // e.g., load balancer return 503 with html body causing server parse error.
    .catch(async (e) => {
      if (includes(GATEWAY_ERRORS, e.statusCode) && tryNum < MAX_RETRIES) {
        retryInfo(`Fetch retry, attempt: ${tryNum}, reason: ${e.statusCode} - ${e.message}`);
        await wait(RETRY_DELAYS[Math.min(RETRY_DELAYS.length - 1, tryNum)]);
        return fetchWithRetry(req, reqinit, tryNum + 1, timeoutMs);
      }
      throw e;
    });
  if (includes(GATEWAY_ERRORS, resp.status) && tryNum < MAX_RETRIES) {
    retryInfo(`Fetch retry, attempt: ${tryNum}, reason: ${resp.status}`);
    await wait(RETRY_DELAYS[Math.min(RETRY_DELAYS.length - 1, tryNum)]);
    return fetchWithRetry(req, reqinit, tryNum + 1, timeoutMs);
  }

  debug(`fetchWithRetry status: ${resp.status}`);

  return resp;
};

/**
 * Is query subscirption
 */
const isSubscription = (query: DocumentNode) => {
  const definition = getMainDefinition(query);
  return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
};

/**
 * Remove __typename in the graphql variables
 *
 * https://stackoverflow.com/questions/47211778/cleaning-unwanted-fields-from-graphql-responses/51380645#51380645
 */
export const cleanTypeName = () =>
  new ApolloLink((operation, forward) => {
    if (operation.variables) {
      const omitTypename = (key, value) => (key === '__typename' ? undefined : value);
      operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
    }
    return forward(operation);
  });

/**
 * Transform ID related fields to local ID
 */
export const toLocalIDLink = () =>
  new ApolloLink((operation, forward) => {
    if (operation.variables) {
      const parseGlobalID = (key, value) => (isIDField(key) ? toLocalIDFilter(value) : value);
      operation.variables = JSON.parse(JSON.stringify(operation.variables), parseGlobalID);
    }
    return forward(operation);
  });

export interface ApolloClientOptions {
  /**
   * Graphql endpoint
   */
  uri?: string;

  /**
   * NextPage Context
   */
  context?: NextPageContext;

  /**
   * InMemoryCache config
   */
  cacheConfiguration?: InMemoryCacheConfig;

  /**
   * Websocket config used for graphql subscriptions
   */
  wsConfig?: WebSocketLink.Configuration | SubscriptionClient;

  /**
   * Websocket client
   */
  graphqlWSConfig?: ClientOptions;

  /**
   * Remove __typename in variables before sending request
   */
  enableCleanTypeName?: boolean;

  /**
   * Transform all variables ID to local ID
   */
  enableToLocalID?: boolean;

  /**
   * Disable global ID
   */
  disableGlobalID?: boolean;

  /**
   * Retry numbers
   */
  retryNumber?: number;

  /**
   * Timeout milliseconds
   */
  timout?: number;

  /**
   * Track where request come.
   */
  requestSourceIdentifier?: string;

  /**
   * Client name for request
   */
  clientName?: ClientNameEnum;
}

export const setGraphQlErrors = (graphQLErrors?: readonly GraphQLError[]) => {
  if (graphQLErrors) {
    const err = graphQLErrors?.[0];
    withScope((scope) => {
      scope.setTag('type', 'GraphQLError');
      scope.setTag('code', err?.extensions?.code as string);
      scope.setTag('module', 'GraphQL');
      scope.setExtra('message', err?.message);
      scope.setExtra('Error', graphQLErrors);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      scope.setLevel('error' as any);
      captureMessage(err?.message);
    });
  }
};

export const setNetworkError = (networkError?: NetworkError) => {
  if (networkError) {
    withScope((scope) => {
      scope.setTag('type', 'GraphQLNetworkError');
      scope.setTag('module', 'GraphQL');
      captureException(networkError);
    });
  }
};

/**
 * Generate common apollo client links.
 * @param options
 */
export const getApolloClientLinks = (options: ApolloClientOptions): ApolloLink[] => {
  const {
    uri,
    context,
    wsConfig,
    graphqlWSConfig,
    enableCleanTypeName,
    enableToLocalID,
    requestSourceIdentifier,
    clientName,
  } = options;
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    setGraphQlErrors(graphQLErrors);
    setNetworkError(networkError);

    // Jerry2 backend sends all errors as network errors.
    // Check if a graphql error was returned along with the network error and override
    // its message.
    if (networkError && graphQLErrors?.[0].message) {
      networkError.message = graphQLErrors?.[0].message;
    }
  });

  const cookie = context?.req?.headers?.cookie;
  const cookieObj = cookie && Cookie.parse(cookie);
  const documentCookie = typeof document !== 'undefined' ? document.cookie : '';
  const documentCookieObj = documentCookie && Cookie.parse(documentCookie);
  const cognitoIdToken = (context?.req?.headers[COGNITO_ID_TOKEN_HEADER_NAME] ||
    cookieObj?.[COGNITO_ID_TOKEN_HEADER_NAME] ||
    documentCookieObj?.[COGNITO_ID_TOKEN_HEADER_NAME]) as string;

  const httpLink = new HttpLink({
    uri,
    credentials: 'same-origin',
    headers: {
      ...(cookie ? { cookie } : {}),
      ...(cognitoIdToken ? { [COGNITO_ID_TOKEN_HEADER_NAME]: cognitoIdToken } : {}),
      ...(options.disableGlobalID ? { [DISABLE_GLOBAL_ID_HEADER]: 'true' } : {}),
      ...(requestSourceIdentifier ? { [JERRY_REQUEST_SOURCE_HEADER]: requestSourceIdentifier } : {}),
      ...(clientName ? { [CLIENT_HEADER_NAME]: clientName } : {}),
    },
    fetch: (req, reqinit) => {
      return fetchWithRetry(req as RequestInfo, reqinit, options.retryNumber, options.timout);
    },
  });

  let link = concat(errorLink, httpLink);

  /**
   * Add subscription websocket link
   *
   * https://github.com/vercel/next.js/issues/5354#issuecomment-520305040
   * https://github.com/apollographql/subscriptions-transport-ws/issues/333#issuecomment-359261024
   */
  if (wsConfig && typeof window !== 'undefined') {
    const wsLink = new WebSocketLink(wsConfig);

    // The split function takes three parameters:
    //
    // * A function that's called for each operation to execute
    // * The Link to use for an operation if the function returns a "truthy" value
    // * The Link to use for an operation if the function returns a "falsy" value
    link = split(({ query }) => isSubscription(query), wsLink, link);
  } else if (graphqlWSConfig && typeof window !== 'undefined') {
    const wsLink = new GraphQLWsLink(createClient(graphqlWSConfig));

    // The split function takes three parameters:
    //
    // * A function that's called for each operation to execute
    // * The Link to use for an operation if the function returns a "truthy" value
    // * The Link to use for an operation if the function returns a "falsy" value
    link = split(({ query }) => isSubscription(query), wsLink, link);
  }

  /**
   * Construct all needed apollo links
   */
  return compact([enableToLocalID && toLocalIDLink(), enableCleanTypeName && cleanTypeName(), link]);
};

/**
 * Create a Apollo client.
 * @param options - apollo options.
 */
export function createApolloClient(options: ApolloClientOptions = {}): ApolloClient<NormalizedCacheObject> {
  debug(`create apollo client with options: ${options}`);
  const { cacheConfiguration } = options;
  const links = getApolloClientLinks(options);
  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: ApolloLink.from(links),
    cache: new InMemoryCache(cacheConfiguration),
  });
}

/**
 * Function to initialize apollo.
 */
export function initializeApollo(
  context?: NextPageContext,
  initialState = {},
  uri = process.env.NEXT_PUBLIC_API_URL
): ApolloClient<NormalizedCacheObject> {
  debug(`init apollo start`);

  const _apolloClient = apolloClient ?? createApolloClient({ uri, context });

  debug(`apollo cache hydration start`);
  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) => sourceArray.every((s) => !isEqual(d, s))),
      ],
    });
    // Restore the cache with the merged data
    _apolloClient.cache.restore(data);
  }
  debug(`apollo cache hydration end`);

  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return _apolloClient;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
}

/**
 * Hook for apollo.
 */
export function useApollo(
  initialState = {},
  uri = process.env.NEXT_PUBLIC_API_URL
): ApolloClient<NormalizedCacheObject> {
  return useMemo(() => initializeApollo(undefined, initialState, uri), [initialState, uri]);
}

/**
 * Explicitly grab cookie value using passed in `name` param.
 * This is needed since returned cookie value from server does not contain ';' as delimiter between keys.
 */
const getCookieByName = (name, cookie) => {
  const value = `; ${cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift();
  } else {
    return undefined;
  }
};

/**
 * Initialize SSR client.
 * Uses custom link that will save 'jerry' session cookie value from API response.
 */
export const initSSRClient = (
  context?: NextPageContext,
  cookieValue?: string,
  uri = process.env.NEXT_PUBLIC_API_URL
) => {
  const cookie = cookieValue || context?.req?.headers?.cookie;

  const getSessionLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((data) => {
      const headers = operation.getContext().response.headers.get('set-cookie');

      // Get jerry cookie value.
      const jerryCookie = getCookieByName('jerry', headers);
      const jerryauth = getCookieByName('jerryauth', headers);
      // Headers format: AWSALB=pD0xNtnHbVUNij/Q2nsFAPXxsmcAjBOG15pKiksZQSoBDzYJWJ2/gZ1qIxa0uEoduhP7LK5XMXdoshE00cdRWSJfL40apWFjDsrQglvrB+uV7BZn310X1+voTcuL; Expires=Tue, 09 Apr 2024 07:39:30 GMT; Path=/, AWSALBCORS=pD0xNtnHbVUNij/Q2nsFAPXxsmcAjBOG15pKiksZQSoBDzYJWJ2/gZ1qIxa0uEoduhP7LK5XMXdoshE00cdRWSJfL40apWFjDsrQglvrB+uV7BZn310X1+voTcuL; Expires=Tue, 09 Apr 2024 07:39:30 GMT; Path=/; SameSite=None; Secure, prefill_quoting_variation=2f7c67d1-81a9-490b-b4c6-db43bbe1e8b8_1; Path=/, jerry=s%3Aupjxpzpb7w-2Wi9xCQHQeb1DTllOq5Ey.OPyXWczq0jpoYGqZVrsJCaVs7Yo2zxIsJqggzQFMNv8; Path=/; Expires=Tue, 02 Jul 2024 07:39:31 GMT
      const prefillQuotingVariation = headers?.match(/prefill_quoting_variation=([^;]*)/)?.[1].trim();

      // Set 'jerry' cookie value into data.
      // Note: Ideally we should be setting the 'jerry' cookie value directly into res.header.
      // We are returning it as part of data since in later steps of server side rendering,
      // We set new cookie value of `jsfeat` that would override the cookie set here.
      // To overcome this issue, we are returning the 'cookie' value and concat it with 'jsfeat' value later.
      data['jerry'] = jerryCookie;
      data['jerryauth'] = jerryauth || '';
      data['prefillQuotingVariation'] = prefillQuotingVariation;
      return { data };
    });
  });

  const baseHttpLink = new HttpLink({
    uri,
    credentials: 'same-origin',
    headers: {
      ...(cookie ? { cookie } : {}),
    },
  });

  return new ApolloClient({
    link: ApolloLink.from([getSessionLink.concat(baseHttpLink)]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
      },
      query: {
        fetchPolicy: 'no-cache',
      },
      mutate: {
        fetchPolicy: 'no-cache',
      },
    },
    ssrMode: true,
  });
};
